/*@flow*/
import type { Element } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import useCommands from './useCommands';
import useContextState from './useContextState';
import LTCloseButton from './LTCloseButton';
import * as I from 'immutable';
import truncate from 'underscore.string/truncate';
import cx from 'classnames';

function LTLayersBodyActiveLayers() {
  const { layersActiveLayers, layersLoaded } = useContextState();
  const h = layersActiveLayers.reduce(
    (s, id) => ({ ...s, [String(id)]: true }),
    {}
  );
  const layers = layersLoaded.filter((v) => h[v.uuid]);
  if (!layers.length) return null;

  return (
    <div
      data-testid="LTLayersBodyActiveLayers"
      className="pl-4 pr-4 mt-4 border-bottom border-light-white pb-3"
    >
      <div className="mb-2">ACTIVE ON MAP</div>
      <div>
        {layers.map(({ uuid, name }, i) => (
          <LTLBCGroupBodyCheckbox key={i} uuid={uuid} name={name} />
        ))}
      </div>
    </div>
  );
}

function useLayerActive(id) {
  const { layersActiveLayers } = useContextState();
  return layersActiveLayers.has(id);
}

function LTLBCGroupBodyCheckbox({ name, uuid }) {
  const cmd = useCommands();
  const checked = useLayerActive(uuid);
  const cs = { checked };

  return (
    <div
      data-testid="LTLBCGroupBodyCheckbox"
      className={cx('checkbox-item cursor-pointer pb-2 pt-2', cs)}
    >
      <label className="align-items-center d-flex cursor-pointer m-0">
        <span
          className="user-select-none"
          css={css('font-size: 1.4rem; user-select: none;')}
        >
          {name}
        </span>
        <input
          type="checkbox"
          className="checkbox-control ml-auto"
          value="on"
          checked={checked}
          onChange={() => {
            cmd.layerToggle(uuid);
          }}
        />
      </label>
    </div>
  );
}

function LTLBCGroupHeader({ name }) {
  const { layersActiveLayerGroup } = useContextState();
  const isOpen = layersActiveLayerGroup === name;
  const cmd = useCommands();

  function onClick() {
    cmd.set(['layersActiveLayerGroup'], isOpen ? null : name);
  }

  return (
    <a
      data-testid="LTLBCGroupHeader"
      className="d-flex justify-content-between align-items-center mb-3 pr-4 mr-1"
      onClick={onClick}
    >
      <span className="user-select-none">{name}</span>
      <span css={css('font-size: 1rem;')}>
        <i
          className={cx('fa', {
            'fa-chevron-down': isOpen,
            'fa-chevron-right': !isOpen,
          })}
        />
      </span>
    </a>
  );
}

function LTLBCGroup({ item }) {
  const { layers, name } = item;
  const { layersActiveLayerGroup } = useContextState();
  const isOpen = layersActiveLayerGroup === name;

  return (
    <div
      data-testid="LTLBCGroup"
      className="mb-3"
      css={css(`
        .checkbox-item {
          cursor: pointer;
          padding: 0 1.75rem 0 1rem;
          &:hover {
            background-color: #6394c480;
          }
          &.checked {
            background-color: #6394c4a6;
          }
        }
      `)}
    >
      <LTLBCGroupHeader name={name} />
      {isOpen && (
        <div
          className="border-left border-light-white"
          css={css('border-left-width: 3px !important;')}
        >
          {layers.map((layer, i) => (
            <LTLBCGroupBodyCheckbox
              key={i}
              uuid={layer.uuid}
              name={truncate(layer.name, 32)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type LayerType = { name: string, id: ID, uuid: string, collection: string };
type CollectionType = {
  name: string,
  layers: LayerType[],
};

// Abbrev: LTLBC
function LTLayersBodyCollections() {
  const { layersLoaded } = useContextState();
  const items: CollectionType[] = (I.List(layersLoaded)
    .filter((v) => v.collection === 'publicData')
    .groupBy((v) => v.layergroup)
    .map((layers, name) => ({ name, layers }))
    .toList()
    .sortBy((v) => v.name)
    .toJS(): any);

  if (items.length === 0) return null;

  return (
    <div data-testid="LTLayersBodyCollections" className="mt-4 pl-4">
      {items.map((item, i) => (
        <LTLBCGroup item={item} key={i} />
      ))}
    </div>
  );
}

function LTLayersBody() {
  return (
    <div data-testid="LTLayersBody">
      <LTLayersBodyActiveLayers />
      <LTLayersBodyCollections />
    </div>
  );
}

export default function LTLayers(): Element<string> {
  return (
    <div
      data-testid="LTLayers"
      css={css(`
      color: #333;
    `)}
    >
      <div className="p-4 d-flex border-bottom border-light-white justify-content-between align-items-center">
        <h5 className="text-uppercase m-0 font-weight-bold">
          AVAILABLE MAP LAYERS
        </h5>
        <LTCloseButton color="#333" />
      </div>
      <LTLayersBody />
    </div>
  );
}
